// A product turned half round in its own frame (x → −x, y → −y), never
// mirrored: its plan drawing, its stored 3D model, its connection points, its
// parts' offsets. The products that stand free of a room (steam generators,
// essence dosers, the Spa Manager) are made with their front to −y, so dropped
// at Rotation Z 0 they face south (docs/claude/products.md, *Front to the
// south*); their generators draw them as the manuals do and turn the result.
// Pure (test/turnHalf.test.mjs).

import type {
  Bounds,
  ConnPort,
  GeomEntity,
  Geometry,
  PartModel3d,
  PartSolid3d,
  Vec2,
} from './types';

const neg = (p: Vec2): Vec2 => ({ x: -p.x, y: -p.y });
const angle = (deg: number): number => (((deg + 180) % 360) + 360) % 360;

/** A drawing's bounds turned. */
export function turnBounds(b: Bounds): Bounds {
  return { min: { x: -b.max.x, y: -b.max.y }, max: { x: -b.min.x, y: -b.min.y } };
}

/** One drawing entity turned. A text keeps reading upright: its anchor turns
 *  and its alignment flips, so its box lands where the turned box would. */
export function turnEntity(e: GeomEntity): GeomEntity {
  switch (e.kind) {
    case 'line': return { ...e, a: neg(e.a), b: neg(e.b) };
    case 'polyline': return { ...e, points: e.points.map(neg) };
    case 'circle': return { ...e, center: neg(e.center) };
    case 'arc': return { ...e, center: neg(e.center), startAngle: angle(e.startAngle), endAngle: angle(e.endAngle) };
    case 'ellipse': return { ...e, center: neg(e.center), major: neg(e.major) };
    case 'spline': return { ...e, control: e.control.map(neg) };
    case 'hatch': return { ...e, loops: e.loops.map((l) => l.map(neg)) };
    case 'text': {
      const { align, baseline, ...rest } = e;
      const a = align === 'center' ? 'center' : align === 'right' ? undefined : 'right';
      const b = baseline === 'middle' ? 'middle' : baseline === 'top' ? undefined : 'top';
      return { ...rest, pos: neg(e.pos), ...(a ? { align: a } : {}), ...(b ? { baseline: b } : {}) };
    }
  }
}

export function turnGeometry(g: Geometry): Geometry {
  return { ...g, entities: g.entities.map(turnEntity), ...(g.bounds ? { bounds: turnBounds(g.bounds) } : {}) };
}

export function turnSolid(s: PartSolid3d): PartSolid3d {
  switch (s.kind) {
    case 'box': return { ...s, x0: -s.x1, x1: -s.x0, y0: -s.y1, y1: -s.y0 };
    case 'cyl':
      return s.axis === 'z'
        ? { ...s, at: [-s.at[0], -s.at[1]] }
        : { ...s, at: [-s.at[0], s.at[1]], from: -s.to, to: -s.from };
    case 'hull': return { ...s, pts: s.pts.map(([x, y, z]) => [-x, -y, z]) };
    case 'pipe': return { ...s, path: s.path.map(([x, y, z]) => [-x, -y, z]) };
    case 'prism': return { ...s, ring: s.ring.map(neg), ...(s.holes ? { holes: s.holes.map((h) => h.map(neg)) } : {}) };
    case 'revolve': return { ...s, at: [-s.at[0], s.at[1]], stations: s.stations.map(([t, r]): [number, number] => [-t, r]).reverse() };
  }
}

export function turnModel3d(m: PartModel3d): PartModel3d {
  return { ...m, solids: m.solids.map(turnSolid) };
}

export function turnPort<P extends Pick<ConnPort, 'nx' | 'ny'>>(p: P): P {
  return { ...p, nx: -p.nx || 0, ny: -p.ny || 0 };
}

/** What of a product or a part turns with it. */
interface Turnable {
  geometry?: Geometry;
  model3d?: PartModel3d;
  ports?: ConnPort[];
  offset?: Vec2;
}

/** A product or a part turned half round in its own frame: its drawing, its
 *  model, its ports and (a part) its offset. Its sizes stay. */
export function turnHalf<T extends Turnable>(p: T): T {
  return {
    ...p,
    ...(p.geometry ? { geometry: turnGeometry(p.geometry) } : {}),
    ...(p.model3d ? { model3d: turnModel3d(p.model3d) } : {}),
    ...(p.ports ? { ports: p.ports.map(turnPort) } : {}),
    ...(p.offset ? { offset: { x: -p.offset.x || 0, y: -p.offset.y || 0 } } : {}),
  };
}
